import React, { useState, useEffect } from "react";
import { Receipt, RefreshCw, ChevronLeft, ChevronRight, CheckSquare, Square, Filter, Trash2, ExternalLink, CheckCircle } from "lucide-react";
import { useTranslation } from "../../i18n/hooks/useTranslation";
import { ClientDetailsModal } from "../ClientDetailsModal";
import { formatDate } from "../../utils/dateUtils";
import { getBillStatusColor } from "../../utils/statusColors";
import { BulkActionBar } from "../common/BulkActionBar";
import { useToast } from "../../contexts/ToastContext";
import { IntegrationSourceBadge } from "./IntegrationSourceBadge";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { ProjectBadge } from "../common/ProjectBadge";

interface Bill {
  _id: string;
  billNumber: string;
  supplierId?:
    | {
        _id?: string;
        name: string;
      }
    | string
    | null;
  projectId?:
    | {
        _id?: string;
        projectNumber: string;
        name?: string;
      }
    | string
    | null;
  status: string;
  date?: Date;
  dueDate?: Date;
  total: number;
  amountDue?: number;
  currencyCode?: string;
  syncedFromXero: boolean;
  syncedFromMYOB: boolean;
  syncedFromQuickBooks: boolean;
  syncedFromZohoBooks: boolean;
  syncedFromExtractor: boolean;
  syncedFrom?: "xero" | "myob" | "quickbooks" | "zohobooks";
  extractorExtractionId?: string;
  extractorFileUrl?: string;
  lastSyncedAt?: string;
}

export const Bills: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [bills, setBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedSupplier, setSelectedSupplier] = useState<{
    id?: string;
    name: string;
  } | null>(null);
  const [selectedBillIds, setSelectedBillIds] = useState<Set<string>>(new Set());
  const [showUnmappedOnly, setShowUnmappedOnly] = useState(false);
  const [isMapping, setIsMapping] = useState(false);
  const [updatingBillId, setUpdatingBillId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [billToDelete, setBillToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [billToApprove, setBillToApprove] = useState<string | null>(null);
  const [isApproving, setIsApproving] = useState(false);
  const pageSize = 20;

  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

  useEffect(() => {
    fetchBills();
  }, [currentPage, showUnmappedOnly]);

  const fetchBills = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("token");

      const unmappedParam = showUnmappedOnly ? "&unmapped=true" : "";
      const response = await fetch(
        `${apiUrl}/api/bills?page=${currentPage}&limit=${pageSize}${unmappedParam}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json();

      if (data.success) {
        setBills(data.data);
        setTotalCount(data.pagination?.total || data.data.length);
        setTotalPages(data.pagination?.totalPages || 1);
        // Clear selections when filters change
        setSelectedBillIds(new Set());
      } else {
        setError(data.message || "Failed to fetch bills");
      }
    } catch (err) {
      console.error("Fetch bills error:", err);
      setError("Failed to connect to server");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSelect = (billId: string) => {
    const newSelected = new Set(selectedBillIds);
    if (newSelected.has(billId)) {
      newSelected.delete(billId);
    } else {
      newSelected.add(billId);
    }
    setSelectedBillIds(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedBillIds.size === bills.length) {
      setSelectedBillIds(new Set());
    } else {
      setSelectedBillIds(new Set(bills.map((bill) => bill._id)));
    }
  };

  const handleMapToProject = async (projectId: string | null) => {
    if (selectedBillIds.size === 0) return;

    try {
      setIsMapping(true);
      const token = localStorage.getItem("token");

      const response = await fetch(`${apiUrl}/api/bills/bulk-project`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          billIds: Array.from(selectedBillIds),
          projectId: projectId,
        }),
      });

      const data = await response.json();

      if (data.success) {
        const count = selectedBillIds.size;
        setSelectedBillIds(new Set());
        await fetchBills();
        showToast(
          `Successfully mapped ${count} ${count === 1 ? "bill" : "bills"} to project`,
          "success"
        );
      } else {
        showToast(data.message || "Failed to map bills", "error");
      }
    } catch (err) {
      console.error("Map bills error:", err);
      showToast("Failed to connect to server", "error");
    } finally {
      setIsMapping(false);
    }
  };

  const handleClearMapping = async () => {
    await handleMapToProject(null);
  };

  const handleDeleteClick = (billId: string) => {
    setBillToDelete(billId);
    setShowDeleteConfirm(true);
  };

  const handleBulkDelete = async () => {
    if (selectedBillIds.size === 0) return;

    try {
      setIsDeleting(true);
      const token = localStorage.getItem("token");
      let successCount = 0;
      let failCount = 0;

      // Delete bills one by one (backend doesn't have bulk delete endpoint)
      for (const billId of Array.from(selectedBillIds)) {
        try {
          const response = await fetch(`${apiUrl}/api/bills/${billId}`, {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          const data = await response.json();
          if (data.success) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          failCount++;
        }
      }

      setSelectedBillIds(new Set());
      await fetchBills();

      if (failCount === 0) {
        showToast(`Successfully deleted ${successCount} ${successCount === 1 ? "bill" : "bills"}`, "success");
      } else {
        showToast(`Deleted ${successCount} bills. ${failCount} failed.`, failCount > successCount ? "error" : "info");
      }
    } catch (error) {
      console.error("Bulk delete error:", error);
      showToast("Failed to delete bills", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!billToDelete) return;

    // Handle bulk delete
    if (billToDelete === 'bulk') {
      await handleBulkDelete();
      setShowDeleteConfirm(false);
      setBillToDelete(null);
      return;
    }

    // Handle single delete
    try {
      setIsDeleting(true);
      const token = localStorage.getItem("token");

      const response = await fetch(`${apiUrl}/api/bills/${billToDelete}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        showToast("Bill deleted successfully", "success");
        setShowDeleteConfirm(false);
        setBillToDelete(null);
        await fetchBills();
      } else {
        showToast(data.message || "Failed to delete bill", "error");
      }
    } catch (error) {
      console.error("Delete bill error:", error);
      showToast("Failed to delete bill", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const canDeleteBill = (bill: Bill) => {
    // Can only delete DRAFT, SUBMITTED, or VOIDED bills (not APPROVED or PAID)
    return bill.status === "DRAFT" || bill.status === "SUBMITTED" || bill.status === "VOIDED";
  };

  const canApproveBill = (bill: Bill) => {
    // Can only approve DRAFT or SUBMITTED bills
    return bill.status === "DRAFT" || bill.status === "SUBMITTED";
  };

  const handleApproveClick = (billId: string) => {
    setBillToApprove(billId);
    setShowApproveConfirm(true);
  };

  const handleApproveConfirm = async () => {
    if (!billToApprove) return;

    try {
      setIsApproving(true);
      const token = localStorage.getItem("token");

      const response = await fetch(`${apiUrl}/api/bills/${billToApprove}/approve`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });

      const data = await response.json();

      if (data.success) {
        showToast("Bill approved successfully", "success");
        setShowApproveConfirm(false);
        setBillToApprove(null);
        await fetchBills();
      } else {
        showToast(data.message || "Failed to approve bill", "error");
      }
    } catch (error) {
      console.error("Approve bill error:", error);
      showToast("Failed to approve bill", "error");
    } finally {
      setIsApproving(false);
    }
  };

  const handleIndividualProjectChange = async (billId: string, projectId: string | null) => {
    try {
      setUpdatingBillId(billId);
      const token = localStorage.getItem("token");

      const response = await fetch(`${apiUrl}/api/bills/${billId}/project`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ projectId }),
      });

      const data = await response.json();

      if (data.success) {
        await fetchBills();
        showToast(
          projectId ? "Bill mapped to project successfully" : "Project mapping cleared",
          "success"
        );
      } else {
        showToast(data.message || "Failed to update bill project mapping", "error");
      }
    } catch (err) {
      console.error("Update bill project mapping error:", err);
      showToast("Failed to connect to server", "error");
    } finally {
      setUpdatingBillId(null);
    }
  };

  const formatCurrency = (amount: number, currencyCode?: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode || "USD",
    }).format(amount);
  };

  const formatDateLocal = (date?: Date | string) => {
    if (!date) return t("admin.bills.notAvailable");
    return formatDate(date);
  };

  // Use centralized status colors
  const getStatusBadgeColor = getBillStatusColor;

  const getIntegrationSource = (bill: Bill) => {
    // Note: syncedFromExtractor is handled separately as it's not a standard integration
    if (bill.syncedFrom) {
      return bill.syncedFrom;
    }
    // Fallback to legacy fields
    if (bill.syncedFromXero) return "xero";
    if (bill.syncedFromMYOB) return "myob";
    if (bill.syncedFromQuickBooks) return "quickbooks";
    if (bill.syncedFromZohoBooks) return "zohobooks";
    return null;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="w-12 h-12 rounded-lg bg-primary-50 dark:bg-gray-700 dark:border dark:border-primary-800/50 flex items-center justify-center mr-4">
            <Receipt className="w-6 h-6 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t("admin.bills.title")}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t("admin.bills.description").replace(
                "{count}",
                totalCount.toString(),
              )}
            </p>
          </div>
        </div>

        <button
          onClick={fetchBills}
          disabled={loading}
          className="flex items-center px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
        >
          <RefreshCw
            className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`}
          />
          {t("admin.bills.refresh")}
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-gray-700 dark:border dark:border-red-800/50 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-800 dark:text-error-400">{error}</p>
        </div>
      )}

      {/* Filter Toggle - Always visible (except when loading first page) */}
      {!(loading && currentPage === 1) && (
        <div className="mb-4 flex items-center justify-between">
          <button
            onClick={() => setShowUnmappedOnly(!showUnmappedOnly)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              showUnmappedOnly
                ? "bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800"
                : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600"
            }`}
          >
            <Filter className="w-4 h-4" />
            <span>Show only unmapped</span>
            {showUnmappedOnly && totalCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-primary-600 dark:bg-primary-500 text-white text-xs rounded-full">
                {totalCount}
              </span>
            )}
          </button>
        </div>
      )}

      {loading && currentPage === 1 ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {t("admin.bills.loading")}
          </p>
        </div>
      ) : bills.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <Receipt className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="text-base font-medium mb-1">
            {showUnmappedOnly ? "No unmapped bills found" : t("admin.bills.noBills")}
          </p>
          <p className="text-sm mt-1">
            {showUnmappedOnly ? (
              <>
                All bills are mapped to projects.{" "}
                <button
                  onClick={() => setShowUnmappedOnly(false)}
                  className="text-primary-600 dark:text-primary-400 hover:underline font-medium"
                >
                  Show all bills
                </button>
              </>
            ) : (
              t("admin.bills.syncHint") ||
              "Sync from your financial integration to view bills"
            )}
          </p>
        </div>
      ) : (
        <>
          {/* Bulk Action Bar */}
          <BulkActionBar
            selectedCount={selectedBillIds.size}
            onMapToProject={handleMapToProject}
            onClearMapping={handleClearMapping}
            onDeselectAll={() => setSelectedBillIds(new Set())}
            onBulkDelete={() => {
              if (selectedBillIds.size > 0) {
                setBillToDelete('bulk');
                setShowDeleteConfirm(true);
              }
            }}
            isLoading={isMapping || isDeleting}
          />

          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th className="text-left py-3.5 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider w-12">
                    <button
                      onClick={handleSelectAll}
                      className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors group"
                      aria-label="Select all bills"
                    >
                      {selectedBillIds.size === bills.length && bills.length > 0 ? (
                        <CheckSquare className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                      ) : (
                        <Square className="w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" />
                      )}
                    </button>
                  </th>
                  <th className="text-left py-3.5 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    {t("admin.bills.tableHeaders.billNumber")}
                  </th>
                  <th className="text-left py-3.5 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    {t("admin.bills.tableHeaders.supplier")}
                  </th>
                  <th className="text-left py-3.5 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    {t("admin.bills.tableHeaders.date")}
                  </th>
                  <th className="text-left py-3.5 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    {t("admin.bills.tableHeaders.dueDate")}
                  </th>
                  <th className="text-left py-3.5 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    {t("admin.bills.tableHeaders.status")}
                  </th>
                  <th className="text-left py-3.5 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="text-left py-3.5 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Project
                  </th>
                  <th className="text-right py-3 px-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t("admin.bills.tableHeaders.total")}
                  </th>
                  <th className="text-right py-3 px-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t("admin.bills.tableHeaders.amountDue")}
                  </th>
                  <th className="text-center py-3.5 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {bills.map((bill) => {
                  return (
                    <tr
                      key={bill._id}
                      className={`border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                        selectedBillIds.has(bill._id)
                          ? "bg-primary-50/50 dark:bg-primary-900/10"
                          : ""
                      }`}
                    >
                      <td className="py-4 px-4">
                        <button
                          onClick={() => handleToggleSelect(bill._id)}
                          className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors group"
                          aria-label="Select bill"
                        >
                          {selectedBillIds.has(bill._id) ? (
                            <CheckSquare className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                          ) : (
                            <Square className="w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" />
                          )}
                        </button>
                      </td>
                      <td className="py-4 px-4 text-sm font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                        {bill.billNumber}
                      </td>
                      <td className="py-4 px-4 text-sm whitespace-nowrap">
                        {typeof bill.supplierId === "object" &&
                        bill.supplierId &&
                        bill.supplierId.name ? (
                          <button
                            onClick={() => {
                              const supplier = bill.supplierId as {
                                _id?: string;
                                name: string;
                              };
                              setSelectedSupplier({
                                id: supplier._id,
                                name: supplier.name,
                              });
                            }}
                            className="text-accent-600 dark:text-accent-400 hover:text-accent-800 dark:hover:text-accent-300 hover:underline cursor-pointer"
                          >
                            {bill.supplierId.name}
                          </button>
                        ) : (
                          <span className="text-gray-900 dark:text-gray-100">
                            {t("admin.bills.notAvailable")}
                          </span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {formatDateLocal(bill.date)}
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {formatDateLocal(bill.dueDate)}
                      </td>
                      <td className="py-4 px-4">
                        <span
                          className={`inline-block px-2 py-1 text-xs font-semibold rounded ${getStatusBadgeColor(bill.status)}`}
                        >
                          {bill.status}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {bill.syncedFromExtractor ? (
                          <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300">
                            Auto-extracted
                          </span>
                        ) : (
                          <IntegrationSourceBadge source={getIntegrationSource(bill)} />
                        )}
                      </td>
                      <td className="py-4 px-4 text-sm whitespace-nowrap">
                        <ProjectBadge
                          projectId={typeof bill.projectId === 'object' && bill.projectId ? bill.projectId._id : bill.projectId as string | null}
                          projectName={typeof bill.projectId === 'object' && bill.projectId ? `${bill.projectId.projectNumber}${bill.projectId.name ? ` - ${bill.projectId.name}` : ''}` : undefined}
                          onChange={(projectId) => handleIndividualProjectChange(bill._id, projectId)}
                          disabled={updatingBillId === bill._id}
                        />
                      </td>
                      <td className="py-4 px-4 text-sm text-right font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                        {formatCurrency(bill.total, bill.currencyCode)}
                      </td>
                      <td className="py-4 px-4 text-sm text-right font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                        {formatCurrency(bill.amountDue || 0, bill.currencyCode)}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center justify-center gap-2">
                          {bill.extractorFileUrl && (
                            <a
                              href={bill.extractorFileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors"
                              title="View original document"
                              aria-label="View original document"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                          {canApproveBill(bill) && (
                            <button
                              onClick={() => handleApproveClick(bill._id)}
                              className="p-1.5 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-md transition-colors"
                              title="Approve bill"
                              aria-label="Approve bill"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                          {canDeleteBill(bill) ? (
                            <button
                              onClick={() => handleDeleteClick(bill._id)}
                              className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                              title="Delete bill"
                              aria-label="Delete bill"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          ) : (
                            !bill.extractorFileUrl && !canApproveBill(bill) && (
                              <span className="text-gray-400 dark:text-gray-600 text-xs" title={`No actions available for ${bill.status.toLowerCase()} bills`}>
                                —
                              </span>
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {t("admin.bills.pagination")
                  .replace("{current}", currentPage.toString())
                  .replace("{total}", totalPages.toString())
                  .replace("{count}", totalCount.toString())}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={currentPage === 1 || loading}
                  className="flex items-center px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  {t("admin.bills.previous")}
                </button>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage === totalPages || loading}
                  className="flex items-center px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {t("admin.bills.next")}
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Supplier Details Modal */}
      {selectedSupplier && (
        <ClientDetailsModal
          isOpen={!!selectedSupplier}
          onClose={() => setSelectedSupplier(null)}
          clientId={selectedSupplier.id}
          clientName={selectedSupplier.name}
          isSupplier={true}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title={billToDelete === 'bulk' ? "Delete Bills" : "Delete Bill"}
        message={
          billToDelete === 'bulk'
            ? `Are you sure you want to delete ${selectedBillIds.size} ${selectedBillIds.size === 1 ? "bill" : "bills"}? This action cannot be undone. Only DRAFT, SUBMITTED, or VOIDED bills can be deleted.`
            : "Are you sure you want to delete this bill? This action cannot be undone."
        }
        confirmLabel={isDeleting ? "Deleting..." : "Delete"}
        cancelLabel="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setBillToDelete(null);
        }}
        confirmButtonClass="bg-red-600 hover:bg-red-700 text-white"
        type="danger"
      />

      {/* Approve Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showApproveConfirm}
        title="Approve Bill"
        message="Are you sure you want to approve this bill? Once approved, the bill will be synced to your accounting integration if configured."
        confirmLabel={isApproving ? "Approving..." : "Approve"}
        cancelLabel="Cancel"
        onConfirm={handleApproveConfirm}
        onCancel={() => {
          setShowApproveConfirm(false);
          setBillToApprove(null);
        }}
        confirmButtonClass="bg-green-600 hover:bg-green-700 text-white"
        type="info"
      />
    </div>
  );
};
